// Remembers which System › Links slug brought this visitor, for the link's
// attribution window. Only the slug is stored; every discount decision is
// made by the database at checkout.

const KEY = "podgo.link";

interface Stored { slug: string; until: number; c?: string; v?: string }

export function rememberLink(slug: string, days: number, now = Date.now(), creative?: string | null, variant?: string | null): void {
  if (!/^[a-z0-9][a-z0-9-]{0,62}$/.test(slug)) return;
  // Artwork code that brought the visitor, so results count per graphic.
  const c = creative && /^[a-z0-9]{4,12}$/.test(creative) ? creative : undefined;
  // Split-test page the visitor was shown, so conversions count per page.
  const v = variant && /^[a-z0-9]{4,12}$/.test(variant) ? variant : undefined;
  try {
    if (days <= 0) {
      sessionStorage.setItem(KEY, JSON.stringify({ slug, c, v, until: Number.MAX_SAFE_INTEGER }));
      return;
    }
    localStorage.setItem(KEY, JSON.stringify({ slug, c, v, until: now + days * 86_400_000 }));
  } catch { /* private mode: attribution is best-effort */ }
}

export function readLink(now = Date.now()): string | null {
  return readStored(now)?.slug ?? null;
}

/** Artwork code remembered with the link, if the visitor came through one. */
export function readLinkCreative(now = Date.now()): string | null {
  return readStored(now)?.c ?? null;
}

/** Split-test page code remembered with the link, if any. */
export function readLinkVariant(now = Date.now()): string | null {
  return readStored(now)?.v ?? null;
}

const STICKY = "podgo.link.variants";

/** The split-test page this browser already got for a slug (kept forever). */
export function stickyVariant(slug: string): string | null {
  try { return (JSON.parse(localStorage.getItem(STICKY) ?? "{}") as Record<string, string>)[slug] ?? null; } catch { return null; }
}

export function keepVariant(slug: string, code: string | null | undefined): void {
  if (!code || !/^[a-z0-9]{4,12}$/.test(code)) return;
  try {
    const all = JSON.parse(localStorage.getItem(STICKY) ?? "{}") as Record<string, string>;
    all[slug] = code;
    localStorage.setItem(STICKY, JSON.stringify(all));
  } catch { /* private mode */ }
}

function readStored(now: number): Stored | null {
  for (const store of [sessionStorage, localStorage]) {
    try {
      const raw = store.getItem(KEY);
      if (!raw) continue;
      const v = JSON.parse(raw) as Stored;
      if (v?.slug && v.until > now) return v;
      store.removeItem(KEY);
    } catch { /* ignore */ }
  }
  return null;
}

export function forgetLink(): void {
  try { localStorage.removeItem(KEY); sessionStorage.removeItem(KEY); } catch { /* ignore */ }
}

export interface LinkOffer {
  discount_type: "percent" | "amount" | string;
  percent_off: number | null;
  amount_off_cents: number | null;
  plan_keys: string[];
  intervals: string[];
}

/** Discounted price, or null when the offer doesn't cover this plan/interval. */
export function discountedPrice(
  offer: LinkOffer | null | undefined,
  planKey: string | undefined,
  interval: "month" | "year",
  amount: number,
): number | null {
  if (!offer || !planKey || amount <= 0) return null;
  if (offer.plan_keys.length > 0 && !offer.plan_keys.includes(planKey)) return null;
  if (!offer.intervals.includes(interval)) return null;
  const off = offer.discount_type === "percent"
    ? amount * (Number(offer.percent_off) / 100)
    : (offer.amount_off_cents ?? 0) / 100;
  return Math.max(0, Math.round((amount - off) * 100) / 100);
}

export function offerLabel(offer: LinkOffer): string {
  return offer.discount_type === "percent"
    ? `${Number(offer.percent_off)}% off`
    : `$${((offer.amount_off_cents ?? 0) / 100).toFixed(2)} off`;
}
